/**
 * mmc_dashboard.js
 * Visual Home/Dashboard for Madmoco Toolbox
 */

import React from 'react';
import { Lightning, ShieldCheck, Cpu, ArrowRight, Star, HardDrive } from '@phosphor-icons/react';
import { MmcButton, MmcCard, MmcGrid, MmcStack, MmcBadge } from './ui';

const MmcDashboard = ({ modules, onSelectModule }) => {
    // Get a few featured modules
    const featured = modules.slice(0, 3);

    return (
        <div className="mmc-dashboard">
            <MmcStack gap={48}>
                {/* Hero Section */}
                <header className="mmc-dashboard__hero">
                    <h1 className="mmc-dashboard__title">
                        EXPAND YOUR <br />
                        <span className="mmc-text-outline">WORKFLOW</span>
                    </h1>
                    <p className="mmc-dashboard__subtitle">
                        A high-density collection of private, client-side tools for the modern web architect.
                        No servers. No tracking. Pure logic.
                    </p>
                </header>

                {/* Values / Features */}
                <MmcGrid className="mmc-dashboard__values">
                    <div className="mmc-value-card">
                        <Lightning size={32} weight="fill" className="mmc-clr-primary" />
                        <h2>Instant</h2>
                        <p>Zero latency. Every computation happens in your browser.</p>
                    </div>
                    <div className="mmc-value-card">
                        <ShieldCheck size={32} weight="fill" style={{ color: 'var(--mmc-clr-accent)' }} />
                        <h2>Private</h2>
                        <p>Your data never leaves your machine. Fully offline-capable.</p>
                    </div>
                    <div className="mmc-value-card">
                        <Cpu size={32} weight="fill" />
                        <h2>Modern</h2>
                        <p>Built with Brutalist precision for high-density productivity.</p>
                    </div>
                </MmcGrid>

                {/* Quick Start / Featured */}
                <MmcStack gap={24}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Star size={20} weight="bold" />
                        <h2 style={{ textTransform: 'uppercase', fontSize: '1.2rem' }}>Featured Tools</h2>
                    </div>
                    <MmcGrid className="mmc-dashboard__featured">
                        {featured.map(module => (
                            <MmcCard key={module.id} className="mmc-featured-card" onClick={() => onSelectModule(module.id)}>
                                <MmcStack gap={16}>
                                    <div className="mmc-featured-card__header">
                                        <MmcBadge>{module.metadata?.category || 'TOOL'}</MmcBadge>
                                        <ArrowRight size={20} />
                                    </div>
                                    <h3>{module.display_name}</h3>
                                    <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>{module.description}</p>
                                </MmcStack>
                            </MmcCard>
                        ))}
                    </MmcGrid>
                </MmcStack>

                {/* Footer Info */}
                <div className="mmc-dashboard__system-info">
                    <HardDrive size={16} />
                    <span>System v{process.env.REACT_APP_VERSION || '1.0.0'} // Ready for production</span>
                </div>
            </MmcStack>

            <style>{`
                .mmc-dashboard {
                    padding: var(--mmc-sp-16);
                    max-width: 1000px;
                    margin: 0 auto;
                    animation: dashboardFadeIn 0.5s ease-out;
                }

                @keyframes dashboardFadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .mmc-dashboard__title {
                    font-size: clamp(2.5rem, 8vw, 4.5rem);
                    line-height: 0.9;
                    font-weight: 900;
                    margin-bottom: var(--mmc-sp-24);
                }

                .mmc-text-outline {
                    -webkit-text-stroke: 2px var(--mmc-clr-black);
                    color: transparent;
                }

                .mmc-dashboard__subtitle {
                    font-size: 1.25rem;
                    max-width: 600px;
                    font-weight: 500;
                    line-height: 1.4;
                    color: var(--mmc-clr-text-muted);
                }

                .mmc-value-card {
                    padding: var(--mmc-sp-32);
                    background: var(--mmc-clr-white);
                    border: var(--mmc-b-width) solid var(--mmc-clr-black);
                    box-shadow: 6px 6px 0px var(--mmc-clr-black);
                    display: flex;
                    flex-direction: column;
                    gap: var(--mmc-sp-16);
                }

                .mmc-value-card h2 {
                    text-transform: uppercase;
                    font-weight: 800;
                }

                .mmc-featured-card {
                    cursor: pointer;
                    transition: all var(--mmc-transition);
                }

                .mmc-featured-card:hover {
                    box-shadow: 12px 12px 0px var(--mmc-clr-primary);
                    transform: translate(-4px, -4px);
                }

                .mmc-featured-card__header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .mmc-dashboard__system-info {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-family: var(--mmc-font-mono);
                    font-size: 0.7rem;
                    opacity: 0.7;
                    text-transform: uppercase;
                    border-top: 1px dashed var(--mmc-clr-black);
                    padding-top: var(--mmc-sp-24);
                }

                @media (max-width: 768px) {
                    .mmc-dashboard__title { font-size: 3rem; }
                    .mmc-dashboard__subtitle { font-size: 1rem; }
                }
            `}</style>
        </div>
    );
};

export default MmcDashboard;
